/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The Account 360 headline numbers: a row of tiles, each an icon chip, a
 * label, one figure and an optional note. Tiles with no value are dropped by
 * the caller, so a tenant without invoices never sees an empty "Outstanding".
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { v2 as t, V2_DISPLAY_FONT_FAMILY } from "styles/tokens/v2";

export default function Account360KpiStrip({ tiles, accent }) {
  if (!tiles.length) return null;
  return (
    <Box
      component="ul"
      aria-label="Key figures"
      sx={{
        listStyle: "none",
        m: 0,
        mb: 2.5,
        p: 0,
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      }}
    >
      {tiles.map(({ key, icon: Icon, label, value, note, noteTitle, onClick }) => {
        const body = (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: accent.tint, color: accent.ink, flexShrink: 0 }}>
                <Icon size={16} aria-hidden="true" />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: t.text2, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: 1.35 }}>
                {label}
              </Typography>
            </Box>
            <Typography
              sx={{ fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 24, lineHeight: 1.25, fontWeight: 600, color: t.text, mt: 1.25, fontVariantNumeric: "tabular-nums", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {value}
            </Typography>
            {note && (
              <Tooltip title={noteTitle || ""} disableHoverListener={!noteTitle}>
                <Typography component="span" sx={{ display: "inline-block", fontSize: 12.5, color: t.text3, mt: 0.5 }}>
                  {note}
                </Typography>
              </Tooltip>
            )}
          </>
        );
        const tileSx = {
          display: "block",
          width: "100%",
          height: "100%",
          textAlign: "left",
          p: 1.75,
          borderRadius: "10px",
          border: `1px solid ${t.border}`,
          bgcolor: t.surface,
          font: "inherit",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: "transparent",
            transition: "background-color 120ms",
          },
        };
        return (
          <li key={key}>
            {onClick ? (
              <Box
                component="button"
                type="button"
                onClick={onClick}
                sx={{
                  ...tileSx,
                  cursor: "pointer",
                  "&:hover": { borderColor: t.border2, boxShadow: t.shadow },
                  "&:hover::after": { bgcolor: accent.main },
                  "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 2 },
                }}
              >
                {body}
              </Box>
            ) : (
              <Box sx={tileSx}>{body}</Box>
            )}
          </li>
        );
      })}
    </Box>
  );
}

Account360KpiStrip.propTypes = {
  tiles: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      note: PropTypes.node,
      noteTitle: PropTypes.string,
      onClick: PropTypes.func,
    })
  ).isRequired,
  accent: PropTypes.shape({ main: PropTypes.string, ink: PropTypes.string, tint: PropTypes.string }).isRequired,
};
